import React, { useState, useEffect, useCallback } from "react";

// Native (design) size of the chat card inside the embedded app
const CHAT_WIDTH = 348;
const CHAT_HEIGHT = 598;
const OFFSET_X = 58;
const OFFSET_Y = 20;
// Frame must be at least card size + offset, or you get a blank strip
const FRAME_WIDTH = 428;
const FRAME_HEIGHT = CHAT_HEIGHT + OFFSET_Y;

const IFRAME_ORIGIN = "https://micaagent.bigfivecrm.com";
const IFRAME_SRC = `${IFRAME_ORIGIN}/?uid=0023006400690038007200030055001F004A001F0022003F0027007600260043005800540002005400760033002F006D0026000000570051000F005200360064006D00250033000E0045000F0052000D0021007B006C003100650047005D00590007005E0055000400140052000D002F00200020007200300016000B0011005F000B003C00030009004A003700150045005D003C0035004F005900450011K3y!9Kye10`;

/**
 * Tracks the visible viewport (handles mobile browser bars / on-screen keyboard)
 * and derives all sizes from it.
 */
const useResponsiveLayout = () => {
  const read = () => {
    if (typeof window === "undefined") return { w: 1280, h: 800 };
    const vv = window.visualViewport;
    return {
      w: Math.round(vv?.width ?? window.innerWidth),
      h: Math.round(vv?.height ?? window.innerHeight),
    };
  };

  const [vp, setVp] = useState(read);

  useEffect(() => {
    const update = () => setVp(read());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    window.visualViewport?.addEventListener("resize", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
      window.visualViewport?.removeEventListener("resize", update);
    };
  }, []);

  const compact = vp.w < 480 || vp.h < 640;
  const margin = compact ? 12 : 24;
  const buttonSize = compact ? 48 : 56;
  const gap = 12; // space between button and chat card
  const bottomOfCard = margin + buttonSize + gap;

  // Room available for the card
  const availW = vp.w - margin * 2;
  const availH = vp.h - bottomOfCard - margin;

  // Only ever scale DOWN so the card never overflows small screens
  const scale = Math.max(
    0.4,
    Math.min(1, availW / CHAT_WIDTH, availH / CHAT_HEIGHT)
  );

  return { margin, buttonSize, bottomOfCard, scale, compact };
};

const MicaOIFrame = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(0); // changes => iframe remounts fresh
  const { margin, buttonSize, bottomOfCard, scale, compact } =
    useResponsiveLayout();

  const closeChat = useCallback(() => {
    setIsOpen(false);
    setSession((s) => s + 1); // reset so next open starts a new chat
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== IFRAME_ORIGIN) return; // only trust our iframe

      const data = event.data;
      const type = typeof data === "string" ? data : data?.type;

      if (["END_CHAT", "CHAT_ENDED", "CLOSE_CHAT"].includes(type)) {
        closeChat();
      }
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [closeChat]);

  // Close on Escape for keyboard users
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === "Escape" && closeChat();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, closeChat]);

  const styles = {
    // Fixed native size, anchored bottom-right, then scaled to fit the viewport.
    crop: {
      position: "fixed",
      right: `calc(${margin}px + env(safe-area-inset-right, 0px))`,
      bottom: `calc(${bottomOfCard}px + env(safe-area-inset-bottom, 0px))`,
      width: `${CHAT_WIDTH}px`,
      height: `${CHAT_HEIGHT}px`,
      transform: `scale(${scale})`,
      transformOrigin: "bottom right",
      overflow: "hidden",
      borderRadius: "24px", // match the chat card's corners
      background: "transparent",
      boxShadow: "none", // a shadow here causes a visible strip
      zIndex: 99999,
    },

    iframe: {
      width: `${FRAME_WIDTH}px`,
      height: `${FRAME_HEIGHT}px`,
      marginLeft: `-${OFFSET_X}px`,
      marginTop: `-${OFFSET_Y}px`,
      border: "none",
      display: "block",
      background: "transparent",
    },

    chatButton: {
      position: "fixed",
      right: `calc(${margin}px + env(safe-area-inset-right, 0px))`,
      bottom: `calc(${margin}px + env(safe-area-inset-bottom, 0px))`,
      width: `${buttonSize}px`,
      height: `${buttonSize}px`,
      borderRadius: "50%",
      border: "none",
      backgroundColor: "#4B4157",
      color: "#FFFFFF",
      fontSize: compact ? "22px" : "26px",
      lineHeight: 1,
      cursor: "pointer",
      boxShadow: "0 4px 18px rgba(0, 0, 0, 0.25)",
      zIndex: 100000,
      touchAction: "manipulation",
      WebkitTapHighlightColor: "transparent",
    },
  };

  return (
    <>
      {isOpen && (
        <div style={styles.crop}>
          <iframe
            key={session}
            src={IFRAME_SRC}
            title="MICA AI Studio"
            style={styles.iframe}
            allow="clipboard-read; clipboard-write"
            allowFullScreen
          />
        </div>
      )}

      <button
        type="button"
        onClick={() => (isOpen ? closeChat() : setIsOpen(true))}
        style={styles.chatButton}
        aria-label={isOpen ? "Close MICA AI Studio" : "Open MICA AI Studio"}
        aria-expanded={isOpen}
      >
        {"✦"}
      </button>
    </>
  );
};

export default MicaOIFrame;